import { useState, useCallback } from "react";
import {
  ChevronRight,
  Scale,
  Info,
  Gavel,
  Scroll,
  CheckCircle2,
  Lightbulb,
  Link as LinkIcon,
  type LucideIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type { ParsedJurisprudence } from "@/utils/jurisprudenceParser";
import { MarkdownRenderer } from "@/features/hector-agent-casediscovery-v3/components/common/MarkdownRenderer";

interface JurisprudenceDropdownProps {
  jurisprudence: ParsedJurisprudence;
  className?: string;
}

/**
 * JurisprudenceDropdown - Collapsible component for displaying a single jurisprudence
 * 
 * Features:
 * - Black rounded header with white text
 * - Expandable/collapsible content
 * - Smooth animations
 * - Keyboard accessible
 * - Organized sections with emojis
 */
export function JurisprudenceDropdown({ jurisprudence, className = "" }: JurisprudenceDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const title = jurisprudence.title || jurisprudence.id;
  const courtBadge = getCourtBadge(title);

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    } else if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, toggleOpen]);

  return (
    <div className={cn(
      "jurisprudence-dropdown rounded-lg",
      isOpen && "shadow-sm",
      className
    )}>
      {/* Header - Collapsible trigger */}
      <button
        id={`jp-header-${jurisprudence.id}`}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200",
          "bg-[#1A1A1A] text-white hover:bg-[#262626] hover:shadow-sm hover:transform hover:-translate-y-0.5",
          "focus:outline-none focus:border-grey-400",
          isOpen && "rounded-b-none shadow-none hover:shadow-none hover:transform-none"
        )}
        role="button"
        aria-expanded={isOpen}
        aria-controls={`jp-content-${jurisprudence.id}`}
        tabIndex={0}
      >
        {/* Chevron icon */}
        <ChevronRight
          className={cn(
            "w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200",
            isOpen && "rotate-90"
          )}
        />
        
        {/* Title — single clean legal reference line. Details stay inside the expanded card. */}
        <span className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold leading-5" title={title}>
          {title}
        </span>

        {courtBadge && (
          <span className="inline-flex h-5 shrink-0 items-center rounded border border-white/20 bg-white/10 px-1.5 text-[10px] font-semibold tracking-[0.08em] text-white">
            {courtBadge}
          </span>
        )}
      </button>

      {/* Content - Expandable area */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`jp-content-${jurisprudence.id}`}
            role="region"
            aria-labelledby={`jp-header-${jurisprudence.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="bg-white rounded-b-lg px-4 py-3 space-y-4 max-h-[640px] overflow-y-auto custom-scrollbar">
              {/* 1. Référence complète — title en texte principal, reference en complément */}
              {jurisprudence.title && (
                <div className="space-y-3">
                  <SectionTitle icon={Scale} title="Référence complète" />
                  <p className="text-sm font-medium text-[#1A1A1A] leading-relaxed">
                    {jurisprudence.title}
                  </p>
                  {jurisprudence.reference && jurisprudence.reference !== jurisprudence.title && (
                    <MarkdownRenderer
                      content={jurisprudence.reference}
                      className="text-xs text-[#666666] leading-relaxed"
                    />
                  )}
                </div>
              )}

              {/* 2. Résumé officiel (Judilibre) */}
              {jurisprudence.officialSummary && (
                <div className="bg-blue-50 p-3 rounded-md border border-blue-100 space-y-2">
                  <SectionTitle icon={Info} title="Résumé officiel (Judilibre)" />
                  <MarkdownRenderer
                    content={jurisprudence.officialSummary}
                    className="text-sm text-blue-900 leading-relaxed italic text-justify"
                  />
                </div>
              )}

              {/* 3. Principes juridiques */}
              {jurisprudence.principles.length > 0 && (
                <div className="space-y-3">
                  <SectionTitle icon={Gavel} title="Principes juridiques dégagés" />
                  <div className="space-y-2.5">
                    {jurisprudence.principles.map((principle, idx) => (
                      <div key={idx} className="bg-[#F8F9FA] p-2.5 rounded border-l-2 border-[#1A1A1A]">
                        <div className="text-sm font-medium text-[#1A1A1A] mb-2">
                          <MarkdownRenderer content={principle.title} className="inline" />
                          {principle.content && (
                            <div className="mt-1 font-normal text-[#4A4A4A]">
                              <MarkdownRenderer content={principle.content} className="text-sm text-[#4A4A4A] leading-relaxed text-justify" />
                            </div>
                          )}
                        </div>
                        {principle.citation && (
                          <div className="text-xs text-[#666666] italic bg-white/50 p-1.5 rounded border border-[#E5E5E5]">
                            <MarkdownRenderer content={principle.citation} className="text-xs text-[#666666] italic" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Solution de la Cour (Dispositif) */}
              {jurisprudence.dispositif && (
                <Section
                  icon={CheckCircle2}
                  title="Dispositif"
                  content={jurisprudence.dispositif}
                />
              )}

              {/* 6. Visa — displays citedArticles content */}
              {jurisprudence.citedArticles && (
                <Section
                  icon={Scroll}
                  title="VISA"
                  content={jurisprudence.citedArticles}
                />
              )}

              {/* 7. Rapprochements */}
              <div className="space-y-3">
                <SectionTitle icon={LinkIcon} title="Jurisprudences de rapprochement" />
                {(jurisprudence.rapprochements?.length ?? 0) > 0 ? (
                  <ul className="list-disc list-inside space-y-1.5">
                    {jurisprudence.rapprochements!.map((r, idx) => (
                      <li key={idx} className="text-sm text-[#4A4A4A] pl-1">
                        {r.url ? (
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:text-accent-blue/80 hover:underline font-medium">
                            {r.text}
                          </a>
                        ) : r.text}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-grey-400 italic">Aucune jurisprudence de rapprochement</p>
                )}
              </div>

              {/* 8. Analyse d'intérêt dans le contexte */}
              {jurisprudence.context && (
                <Section
                  icon={Lightbulb}
                  title="Analyse d'intérêt dans le contexte"
                  content={jurisprudence.context}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Les titres ont DEUX dialectes backend : legacy (« Cour de cassation, 12 mars 2024 ») et
// corpus (COURT_LABELS de toJpEntry.ts : « Cass. », « T. com. », « CA Paris »…). Sans les
// variantes corpus ici, les décisions du corpus perdent silencieusement leur badge.
function getCourtBadge(title: string): string | null {
  if (/^(?:CC\b|Cass\.|Cour de cassation\b)/i.test(title)) return 'CC';
  if (/^(?:CA\b|Cour d'appel\b)/i.test(title)) return 'CA';
  if (/^(?:TJ\b|Tribunal judiciaire\b)/i.test(title)) return 'TJ';
  if (/^(?:TCOM\b|T\. com\.|Tribunal de commerce\b)/i.test(title)) return 'TCOM';
  if (/^(?:CE\b|Conseil d['’]État\b|Conseil d['’]Etat\b)/i.test(title)) return 'CE';
  if (/^(?:CAA\b|Cour administrative d'appel\b)/i.test(title)) return 'CAA';
  if (/^(?:TA\b|Tribunal administratif\b)/i.test(title)) return 'TA';
  return null;
}

/**
 * Section component for consistent formatting with XML tag parsing
 */
function Section({
  icon: Icon,
  title,
  content,
}: {
  icon: LucideIcon;
  title: string;
  content: string;
}) {
  return (
    <div className="space-y-3">
      <SectionTitle icon={Icon} title={title} />
      <MarkdownRenderer
        content={content}
        className="text-sm text-[#4A4A4A] leading-relaxed text-justify"
      />
    </div>
  );
}

/**
 * Section title component with icon
 */
function SectionTitle({ 
  icon: Icon, 
  title
}: { 
  icon: LucideIcon;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-[#E5E5E5] pb-1.5">
      <Icon className="h-3.5 w-3.5 text-[#1A1A1A]" />
      <h4 className="text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">
        {title}
      </h4>
    </div>
  );
}
